import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  CalendarClock,
  FileSearch,
  FileText,
  GraduationCap,
  Library,
  Plus,
  Trophy,
  UserPlus,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";

import {
  acknowledgeDealExpiry,
  discardDealDraft,
  dismissDraftCard,
  dismissOutcomeCard,
  fetchDealDashboard,
  getTeamDashboard,
  getTeamDeals,
  getTrainingMeStatus,
  listExpiringDeals,
  listMyDeals,
  listMyDrafts,
  listRecentAssets,
  snoozeMeetingRewardReminder,
  stopMeetingRewardReminder,
  type Deal,
  type DealDashboardMetrics,
  type ManagerDealListItem,
  type RecentAsset,
  type TrainingMeStatusItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { StatCard } from "@/components/StatCard";
import { TeamInviteDialog } from "@/components/TeamInviteDialog";
import { DealDetailDrawer } from "@/components/deals/DealDetailDrawer";
import {
  DealFeedSidebar,
  deriveExpiredContextCards,
  deriveOutcomeCards,
} from "@/components/deals/DealFeedSidebar";
import { PoTodoFeed } from "@/components/po-dashboard/PoTodoFeed";
import { buildPoFeed } from "@/components/po-dashboard/poFeed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { dealToasts, useToast } from "@/components/ui/toast";
import { FEATURE_NAV_LABEL } from "@/lib/asset-labels";
import { DEMO_MOCK } from "@/lib/demo-flags";
import { useTrainingLockout } from "@/lib/useTrainingLockout";

// Constant-condition ternary so Rollup eliminates BOTH the React.lazy
// registration AND the import() chunk reference when DEMO_MOCK is off.
// A naïve `lazy(() => import("...demo..."))` at module top level would
// emit the demo chunk into dist/ even when never rendered — the dynamic
// import() expression is reachable from the main bundle's lazy() call.
// Putting the entire lazy() inside `DEMO_MOCK ? ... : null` makes the
// import() unreachable at build time when DEMO_MOCK is the literal
// false produced by `import.meta.env.VITE_DEMO_MOCK === "1"` constant-
// folding.
const DemoPartnerDashboard = DEMO_MOCK
  ? lazy(() =>
      import("@/demo/pages/DemoPartnerDashboard").then((m) => ({
        default: m.DemoPartnerDashboard,
      })),
    )
  : null;

/**
 * review-r2 (9c088dcc / 143e5b7a): the dashboard's third metric card is
 * training-aware and steps through three states:
 *   1. Compulsory modules still remaining → "Mandatory training modules remaining"
 *   2. All compulsory done, optional modules remaining → "Additional training
 *      modules available"
 *   3. Everything completed → the card disappears (returns null)
 * Both interactive states deep-link to the training page.
 */
type TrainingCard = { kind: "mandatory" | "additional"; count: number } | null;

export function deriveTrainingCard(modules: TrainingMeStatusItem[]): TrainingCard {
  const mandatoryRemaining = modules.filter(
    (mod) => mod.is_compulsory && mod.status !== "completed",
  ).length;
  if (mandatoryRemaining > 0) return { kind: "mandatory", count: mandatoryRemaining };
  const additionalAvailable = modules.filter(
    (mod) => !mod.is_compulsory && mod.status !== "completed",
  ).length;
  if (additionalAvailable > 0) return { kind: "additional", count: additionalAvailable };
  return null;
}

/**
 * review-r2 (9c088dcc / 143e5b7a / 308a9149 / 69af1cf9): the shared partner
 * metric-card row — identical for the BDM personal view and the PO team view,
 * rendered below the primary buttons and above the feed. Three cards:
 *   - My expiring deals (# closing in less than 30 days)
 *   - Deals in review (count)
 *   - Conditional training card (mandatory-remaining / additional-available /
 *     hidden) — only this one deep-links to the training page; the deal cards
 *     deep-link to the deals ledger.
 */
function PartnerMetricCards({
  metrics,
  training,
}: {
  metrics: DealDashboardMetrics | undefined;
  training: TrainingCard;
}) {
  const expiring = metrics ? metrics.expiring_soon : "—";
  const inReview = metrics ? metrics.pending_review : "—";
  return (
    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Link
        to="/dashboard/partner/deals"
        className="block h-full rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-lifted"
      >
        <StatCard
          title="My expiring deals"
          value={expiring}
          subtitle={metrics ? "Closing in less than 30 days" : undefined}
          icon={CalendarClock}
          testId="bdm-expiring-deals"
          tone="neutral"
          // Colour by exception — a gentle amber edge only when something is
          // closing soon.
          accent={metrics && metrics.expiring_soon > 0 ? "amber" : undefined}
          className="h-full"
        />
      </Link>
      <Link
        to="/dashboard/partner/deals"
        className="block h-full rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-lifted"
      >
        <StatCard
          title="Deals in review"
          value={inReview}
          subtitle={
            metrics
              ? metrics.pending_review > 0
                ? "Awaiting review"
                : "None in review"
              : undefined
          }
          icon={FileSearch}
          testId="bdm-deals-in-review"
          tone="neutral"
          accent={metrics && metrics.pending_review > 0 ? "amber" : undefined}
          className="h-full"
        />
      </Link>
      {training ? (
        <Link
          to="/dashboard/partner/training"
          className="block h-full rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-lifted"
        >
          <StatCard
            title={
              training.kind === "mandatory"
                ? "Mandatory training modules remaining"
                : "Additional training modules available"
            }
            value={training.count}
            icon={GraduationCap}
            testId="bdm-training"
            tone="neutral"
            // CHP-877: outstanding compulsory training is urgent — a red edge;
            // optional extras stay neutral.
            accent={training.kind === "mandatory" ? "red" : undefined}
            className="h-full"
          />
        </Link>
      ) : null}
    </div>
  );
}

export function PartnerDashboard() {
  const { data, accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // po-dashboard: the "Invite your team" card opens the invite modal in place
  // (was a deep-link to /dashboard/partner/team, which still works on its own
  // route). The Main Dashboard lane rewrites this file later.
  const [inviteOpen, setInviteOpen] = useState(false);
  // CHP-868: feed "view deal" actions open the deal detail in a slide-out
  // drawer (same as the deals ledger / elsewhere), not a full-page route.
  const [drawerDealId, setDrawerDealId] = useState<string | null>(null);
  const { toast } = useToast();

  // RACCPE-22: BDMs with outstanding compulsory training can't register
  // deals — the CTA swaps to a finish-training link (same gate as the
  // deals dashboard header). Shares the ["training", "me-status"] cache.
  const lockout = useTrainingLockout();

  // Hooks must be called in stable order every render — the DEMO_MOCK
  // early return below means useQuery calls have to live ABOVE it. In
  // demo builds the queries are gated off via `enabled` so no fetches
  // fire while the demo dashboard renders.
  //
  // Wave 2: the deal-list/drafts/expiring keys deliberately match the
  // deals dashboard (routes/dashboard/partner/deals.tsx) so both surfaces
  // share cache entries and feed invalidations refresh each other.
  const dealsQuery = useQuery({
    queryKey: ["deals", "me", accessToken],
    queryFn: () => listMyDeals(accessToken!),
    enabled: !DEMO_MOCK && !!accessToken,
  });
  const trainingStatusQuery = useQuery({
    queryKey: ["training", "me-status"],
    queryFn: () => getTrainingMeStatus(accessToken!),
    enabled: !DEMO_MOCK && !!accessToken,
  });
  const draftsQuery = useQuery<Deal[]>({
    queryKey: ["deals", "drafts", "me", accessToken],
    queryFn: () => listMyDrafts(accessToken!),
    enabled: !DEMO_MOCK && !!accessToken,
  });
  const expiringQuery = useQuery<Deal[]>({
    queryKey: ["deals", "expiring", accessToken],
    queryFn: () => listExpiringDeals(accessToken!),
    enabled: !DEMO_MOCK && !!accessToken,
  });

  // po-dashboard: a Program Owner (channel_partner with is_org_admin === true)
  // gets the player-coach Main Dashboard — team metrics + a weighted to-do feed.
  // A BDM (is_org_admin === false) keeps the personal view below. We branch in
  // render; the two team queries below gate on `isPo` so a BDM never calls the
  // PO-only /deals/team-* endpoints (which 403 non-POs server-side anyway).
  const isPo = data?.role === "channel_partner" && data.is_org_admin === true;
  const teamDashboardQuery = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "team-dashboard", accessToken],
    queryFn: () => getTeamDashboard(accessToken!),
    enabled: !DEMO_MOCK && !!accessToken && isPo,
  });
  const teamDealsQuery = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", "team-list", "all", accessToken],
    queryFn: () => getTeamDeals(accessToken!, "all"),
    enabled: !DEMO_MOCK && !!accessToken && isPo,
  });
  // BDM personal Main Dashboard scorecard (spec 719224884): the 3 deal-metric
  // cards (Sales YTD / Pipeline / Pending Regs) read the personal dashboard
  // metrics. GET /deals/dashboard scopes to the caller's own deals
  // (Deal.bdm_id == actor.id) server-side. Gated on !isPo — a PO uses the
  // team-dashboard query above instead.
  const dealDashboardQuery = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "dashboard", "me", accessToken],
    queryFn: () => fetchDealDashboard(accessToken!),
    enabled: !DEMO_MOCK && !!accessToken && !isPo,
  });
  // follow-up #62: recently-added sales collateral → the BDM "New Sales
  // Collateral Added" info-tier feed card. Gated on !isPo (BDM personal view
  // only; the PO player-coach feed is its own component).
  const recentAssetsQuery = useQuery<RecentAsset[]>({
    queryKey: ["assets", "recent", accessToken],
    queryFn: async () => (await listRecentAssets(accessToken!)).items,
    enabled: !DEMO_MOCK && !!accessToken && !isPo,
  });

  // Feed mutations — mirrors of the deals dashboard wiring.
  const discard = useMutation({
    mutationFn: (dealId: string) => discardDealDraft(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "drafts"] });
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
  });
  const dismissExpiring = useMutation({
    mutationFn: (dealId: string) => acknowledgeDealExpiry(accessToken!, dealId),
    // review-r2 (9c21a324): closing a feed card must NOT toast — "we do not need
    // a toast message, this goes for all feeds and for all users". The
    // expiring-card [X] now dismisses silently (the card animating away is the
    // only confirmation needed). The error path keeps a toast — a failed close
    // still needs surfacing.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "expiring"] });
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
    onError: () => {
      toast(dealToasts.failed("set the deal to expire"));
    },
  });
  const dismissDraft = useMutation({
    mutationFn: (dealId: string) => dismissDraftCard(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "drafts"] });
    },
  });
  const dismissOutcome = useMutation({
    mutationFn: (dealId: string) => dismissOutcomeCard(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
    },
  });
  // CHP-387: pooled-payout midpoint reminder footer actions. Both re-fetch the
  // org-wide ledger (the feed's source) so the card drops once the reminder is
  // snoozed/stopped.
  const snoozeMeetingReward = useMutation({
    mutationFn: (dealId: string) => snoozeMeetingRewardReminder(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "team-list"] });
    },
    onError: () => {
      toast(dealToasts.failed("snooze the first-meeting reward reminder"));
    },
  });
  const stopMeetingReward = useMutation({
    mutationFn: (dealId: string) => stopMeetingRewardReminder(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals", "team-list"] });
    },
    onError: () => {
      toast(dealToasts.failed("stop the first-meeting reward reminders"));
    },
  });

  // Outcome alerts derive from the same partner list query that feeds the
  // stat cards — no extra request.
  const outcomeDeals = useMemo(() => deriveOutcomeCards(dealsQuery.data ?? []), [dealsQuery.data]);

  // CHP-380: auto-expired deals awaiting a close reason ("DEAL EXPIRED" cards).
  const expiredContextDeals = useMemo(
    () => deriveExpiredContextCards(dealsQuery.data ?? []),
    [dealsQuery.data],
  );

  // Demo flag swap. The wrapper keeps the same export name + default
  // greeting heading so tests/dashboard.test.tsx (which imports
  // PartnerDashboard and asserts "Welcome, alice") still passes when
  // running without VITE_DEMO_MOCK.
  if (DEMO_MOCK && DemoPartnerDashboard) {
    return (
      <Suspense
        fallback={
          <div role="status" className="container py-6">
            Loading…
          </div>
        }
      >
        <DemoPartnerDashboard />
      </Suspense>
    );
  }

  const greeting = data?.full_name ? `, ${data.full_name}` : "";
  // Slice 3 / CHP-295: only POs (channel_partners with a linked Org) can
  // invite their team. Hide the card for partners without an org (409) AND
  // for BDMs (the endpoint now 403s non-org-admins since the form can mint
  // peer POs). is_org_admin !== false keeps null/true as PO semantics.
  const canInviteTeam =
    data?.role === "channel_partner" && Boolean(data.organization) && data.is_org_admin !== false;

  // BDM metric cards (review-r2 143e5b7a) — the personal deal dashboard drives
  // "My expiring deals" (expiring_soon) and "Deals in review" (pending_review).
  const m = dealDashboardQuery.data;

  // Outstanding = compulsory modules past their grace deadline the BDM/PO hasn't
  // completed. Drives the dashboard nudge; the deal-creation lockout itself
  // lands in Slice 6 (RACCPE), so this card is informational for now.
  const outstandingTraining = (trainingStatusQuery.data ?? []).filter((s) => s.outstanding).length;

  // review-r2 (9c088dcc / 143e5b7a): the third metric card is training-aware —
  // mandatory-remaining → additional-available → hidden once everything is done.
  const trainingCard = deriveTrainingCard(trainingStatusQuery.data ?? []);

  // Dismissed drafts stay resumable via the wizard — they just lose the card.
  const activeDrafts = (draftsQuery.data ?? []).filter(
    (d) => d.deal_status === "draft" && d.draft_dismissed_at === null,
  );
  // follow-up #62: recently-added collateral feeding the BDM info-tier card.
  const recentAssets = recentAssetsQuery.data ?? [];
  const hasFeed =
    (expiringQuery.data ?? []).length > 0 ||
    activeDrafts.length > 0 ||
    outcomeDeals.length > 0 ||
    expiredContextDeals.length > 0 ||
    // follow-up #62: the feed must render for a BDM whose only new item is
    // freshly-added sales collateral (no deals yet).
    recentAssets.length > 0 ||
    // CHP-465: the feed must render for a BDM whose only outstanding item is
    // compulsory training (no deals yet) — the training card lives here.
    outstandingTraining > 0;
  // Distinguish "feed loading" from "feed genuinely empty" so the BDM empty
  // state (spec 719224884 — "Clear to Close") only renders once data settles.
  const feedLoading =
    dealsQuery.isLoading ||
    expiringQuery.isLoading ||
    draftsQuery.isLoading ||
    trainingStatusQuery.isLoading ||
    recentAssetsQuery.isLoading;
  // CHP-804: a failed feed query must NOT fall through to the positive
  // "Clear to Close" / "All clear" empty state — that reads as "you're caught
  // up" when data actually failed. Track feed errors (PO additionally depends
  // on the two team queries) and render an error/retry card instead.
  const feedError =
    dealsQuery.isError ||
    expiringQuery.isError ||
    draftsQuery.isError ||
    trainingStatusQuery.isError ||
    recentAssetsQuery.isError ||
    (isPo && (teamDealsQuery.isError || teamDashboardQuery.isError));
  const retryFeed = () => {
    void dealsQuery.refetch();
    void expiringQuery.refetch();
    void draftsQuery.refetch();
    void trainingStatusQuery.refetch();
    void recentAssetsQuery.refetch();
    if (isPo) {
      void teamDealsQuery.refetch();
      void teamDashboardQuery.refetch();
    }
  };

  // po-dashboard: the Program Owner gets the player-coach Main Dashboard.
  // Everything below (team metrics + the weighted to-do feed) is gated on
  // `isPo`; a BDM falls through to the personal view at the bottom of the file.
  if (isPo) {
    return (
      <PartnerProgramOwnerDashboard
        lockout={lockout}
        teamMetrics={teamDashboardQuery.data}
        trainingCard={trainingCard}
        teamDeals={teamDealsQuery.data ?? []}
        ownDealIds={(dealsQuery.data ?? []).map((d) => d.id)}
        drafts={activeDrafts}
        expiring={expiringQuery.data ?? []}
        outcomes={outcomeDeals}
        outstandingTraining={outstandingTraining}
        viewerPayoutPooled={data?.spif_payout_type === "pooled"}
        canInviteTeam={canInviteTeam}
        accessToken={accessToken}
        orgName={data?.organization?.company_name ?? null}
        orgId={data?.organization?.id ?? null}
        inviteOpen={inviteOpen}
        setInviteOpen={setInviteOpen}
        onReviewExpiring={(dealId) =>
          void navigate({ to: "/dashboard/partner/deals", search: { reviewDealId: dealId } })
        }
        onResumeDraft={(draftId) =>
          void navigate({ to: "/dashboard/partner/deals/new", search: { draftId } })
        }
        onOpenTraining={() => void navigate({ to: "/dashboard/partner/training" })}
        onDismissExpiring={(dealId) => dismissExpiring.mutate(dealId)}
        onDismissDraft={(dealId) => dismissDraft.mutate(dealId)}
        onDismissOutcome={(dealId) => dismissOutcome.mutate(dealId)}
        onSnoozeMeetingReward={(dealId) => snoozeMeetingReward.mutate(dealId)}
        onStopMeetingReward={(dealId) => stopMeetingReward.mutate(dealId)}
        feedError={feedError}
        onRetryFeed={retryFeed}
      />
    );
  }

  return (
    <div className="container py-6">
      {/* CHP-878: the BDM dashboard subtitle was removed (the PO view keeps its
          own, different subtitle below). */}
      <h1 className="text-2xl font-semibold tracking-tight">
        {greeting ? `Welcome${greeting}.` : "Welcome"}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {lockout.blocked ? (
          // CHP-889/890 (+876): primary (pink) CTA, "Complete training to
          // register deals".
          <Button asChild data-testid="register-blocked-training">
            <Link to="/dashboard/partner/training">
              <GraduationCap className="h-4 w-4" aria-hidden /> Complete training to register deals
            </Link>
          </Button>
        ) : (
          <Button asChild data-testid="register-deal-cta">
            <Link to="/dashboard/partner/deals/new">
              <Plus className="h-4 w-4" aria-hidden /> Register a deal
            </Link>
          </Button>
        )}
        {/* CHP-879: jump to the full deals ledger, before Explore knowledge base. */}
        <Button asChild variant="outline" data-testid="partner-view-all-deals">
          <Link to="/dashboard/partner/deals">
            <FileText className="h-4 w-4" aria-hidden /> View all deals
          </Link>
        </Button>
        <Button asChild variant="outline" data-testid="explore-sales-toolkit">
          <Link to="/dashboard/assets">
            <Library className="h-4 w-4" aria-hidden /> Explore {FEATURE_NAV_LABEL.toLowerCase()}
          </Link>
        </Button>
        {/* CHP-1005: optional (non-compulsory) training discovery, after the
            knowledge-base CTA. */}
        <Button asChild variant="outline" data-testid="browse-optional-training">
          <Link to="/dashboard/partner/training">
            <GraduationCap className="h-4 w-4" aria-hidden /> Browse optional training
          </Link>
        </Button>
        {/* CHP-788: compact secondary launch for the team-invite Sheet, gated on
            canInviteTeam. Replaces the removed promo card (CHP-790); label
            matches the Insights & Admin route for consistency. */}
        {canInviteTeam && (
          <Button
            variant="outline"
            onClick={() => setInviteOpen(true)}
            data-testid="partner-open-team-invites"
          >
            <UserPlus className="h-4 w-4" aria-hidden /> Invite new team member
          </Button>
        )}
      </div>

      {/* Metric cards — review-r2 (143e5b7a / 308a9149): below the primary
          buttons, above the feed. The new 3-card set (My expiring deals /
          Deals in review / conditional training) replaces the old
          Sales-YTD / Pipeline / Pending-Registrations scorecard. */}
      <PartnerMetricCards metrics={m} training={trainingCard} />

      {/* FEED — same card set as the deals dashboard (expiring + drafts +
          outcome alerts). Resume Form deep-links into the wizard; Review Options
          deep-links to the deals dashboard with the decision overlay open. */}
      <div className="mt-6">
        {hasFeed ? (
          <DealFeedSidebar
            expiring={expiringQuery.data ?? []}
            drafts={activeDrafts}
            outcomes={outcomeDeals}
            expiredContext={expiredContextDeals}
            reviewingDeal={null}
            onReviewOptions={(dealId) =>
              void navigate({
                to: "/dashboard/partner/deals",
                search: { reviewDealId: dealId },
              })
            }
            onCloseReview={() => {}}
            onResumeDraft={(draftId) =>
              void navigate({
                to: "/dashboard/partner/deals/new",
                search: { draftId },
              })
            }
            onDiscardDraft={(dealId) => discard.mutate(dealId)}
            isDiscarding={discard.isPending}
            onDismissExpiring={(dealId) => dismissExpiring.mutate(dealId)}
            onDismissDraft={(dealId) => dismissDraft.mutate(dealId)}
            onDismissOutcome={(dealId) => dismissOutcome.mutate(dealId)}
            // CHP-868: open the deal detail in the slide-out drawer, not a full page.
            onViewOutcome={(dealId) => setDrawerDealId(dealId)}
            outstandingTraining={outstandingTraining}
            onOpenTraining={() => void navigate({ to: "/dashboard/partner/training" })}
            recentAssets={recentAssets}
            onViewAsset={() => void navigate({ to: "/dashboard/assets" })}
          />
        ) : feedLoading ? null : feedError ? (
          // CHP-804: a failed feed query must not read as "all caught up".
          <Card data-testid="feed-error">
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
              <p className="text-sm font-medium text-destructive">Couldn&apos;t load your feed</p>
              <p className="text-sm text-muted-foreground">
                Something went wrong loading your deals and training. Please try again.
              </p>
              <Button variant="outline" size="sm" onClick={retryFeed}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="feed-clear-to-close">
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              <Trophy className="h-8 w-8 text-success" aria-hidden />
              <p className="text-sm font-medium">Clear to Close</p>
              <p className="text-sm text-muted-foreground">
                You&apos;re all caught up — no outstanding deals or training. Register a deal to get
                started.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* CHP-790: the "Invite your team" promo card was removed — the compact
          "Invite new team member" button in the actions row above now triggers
          this Sheet. */}
      {canInviteTeam && accessToken && (
        <TeamInviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          token={accessToken}
          orgName={data?.organization?.company_name ?? null}
          orgId={data?.organization?.id ?? null}
        />
      )}

      {/* CHP-868: feed "view deal" opens the deal detail here, in place. */}
      <DealDetailDrawer dealId={drawerDealId} onClose={() => setDrawerDealId(null)} />
    </div>
  );
}

interface ProgramOwnerDashboardProps {
  lockout: ReturnType<typeof useTrainingLockout>;
  teamMetrics: DealDashboardMetrics | undefined;
  /** review-r2 (9c088dcc / 69af1cf9): the shared conditional training metric
   * card state — mandatory-remaining / additional-available / hidden. */
  trainingCard: TrainingCard;
  teamDeals: ManagerDealListItem[];
  /** The PO's own deal ids — excluded from Tier-1 team feed cards. */
  ownDealIds: string[];
  drafts: Deal[];
  expiring: Deal[];
  outcomes: ReturnType<typeof deriveOutcomeCards>;
  outstandingTraining: number;
  /** CHP-387: viewer's firm runs a pooled incentive model → show the org-deal
   * midpoint reminder cards. */
  viewerPayoutPooled: boolean;
  canInviteTeam: boolean;
  accessToken: string | undefined;
  orgName: string | null;
  orgId: string | null;
  inviteOpen: boolean;
  setInviteOpen: (open: boolean) => void;
  onReviewExpiring: (dealId: string) => void;
  onResumeDraft: (draftId: string) => void;
  onOpenTraining: () => void;
  onDismissExpiring: (dealId: string) => void;
  onDismissDraft: (dealId: string) => void;
  onDismissOutcome: (dealId: string) => void;
  onSnoozeMeetingReward: (dealId: string) => void;
  onStopMeetingReward: (dealId: string) => void;
  /** CHP-804: a feed-source query failed — render an error/retry state in the
   * to-do feed instead of the positive "All clear" empty state. */
  feedError: boolean;
  onRetryFeed: () => void;
}

/**
 * The Program Owner's "player-coach" Main Dashboard.
 *
 * Differs from the BDM personal view: a team-metrics row (light summaries that
 * deep-link into the Insights drawers) and a weighted to-do feed that blends
 * Tier-1 team blockers with Tier-2 personal actions and Tier-3 enablement.
 * The four metric cards do NOT re-implement the Insights surface — they link to
 * /dashboard/partner/insights, where the matching drawer opens.
 */
function PartnerProgramOwnerDashboard({
  lockout,
  teamMetrics,
  trainingCard,
  teamDeals,
  ownDealIds,
  drafts,
  expiring,
  outcomes,
  outstandingTraining,
  canInviteTeam,
  accessToken,
  orgName,
  orgId,
  inviteOpen,
  setInviteOpen,
  onReviewExpiring,
  onResumeDraft,
  onOpenTraining,
  viewerPayoutPooled,
  onDismissExpiring,
  onDismissDraft,
  onDismissOutcome,
  onSnoozeMeetingReward,
  onStopMeetingReward,
  feedError,
  onRetryFeed,
}: ProgramOwnerDashboardProps) {
  // CHP-868: feed "view deal" actions open the deal detail in a slide-out
  // drawer (not a full page). A team member's deal is opened read-only (the PO
  // is not the owner, so owner-only write actions would 404); the PO's own
  // deals open with the owner actions.
  const [drawerDeal, setDrawerDeal] = useState<{ id: string; readOnly: boolean } | null>(null);
  // m11: one reference clock per render, shared by the feed builder and the
  // feed's countdown labels (each previously defaulted its own `new Date()`).
  // CHP-809: the clock ticks every minute (and on window focus) so "expires in
  // N days" / SLA countdowns stay accurate in a long-open session — captured
  // once at mount, they could read a day late without a reload. A minute is
  // ample (the labels are day-granular) and cheap.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = window.setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", tick);
    };
  }, []);
  const feed = useMemo(
    () =>
      buildPoFeed({
        teamDeals,
        ownDealIds,
        drafts,
        expiring,
        outcomes,
        outstandingTraining,
        viewerPayoutPooled,
        now,
      }),
    [
      teamDeals,
      ownDealIds,
      drafts,
      expiring,
      outcomes,
      outstandingTraining,
      viewerPayoutPooled,
      now,
    ],
  );

  return (
    <div className="container py-6">
      {/* CHP-1028: the PO player-coach dashboard is the partner's Insights &
          Admin home — title it "[Channel Partner Name] Insights and Admin"
          rather than the generic BDM "Welcome, {name}." greeting. */}
      <h1 className="text-2xl font-semibold tracking-tight">
        {orgName ? `${orgName} Insights and Admin` : "Insights and Admin"}
      </h1>

      {/* Primary tasks — register a deal (training-lockout swap) + the sales
          toolkit. */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {lockout.blocked ? (
          // CHP-889/890 (+876): primary (pink) CTA, "Complete training to
          // register deals".
          <Button asChild data-testid="register-blocked-training">
            <Link to="/dashboard/partner/training">
              <GraduationCap className="h-4 w-4" aria-hidden /> Complete training to register deals
            </Link>
          </Button>
        ) : (
          <Button asChild data-testid="register-deal-cta">
            <Link to="/dashboard/partner/deals/new">
              <Plus className="h-4 w-4" aria-hidden /> Register a deal
            </Link>
          </Button>
        )}
        {/* CHP-879: jump to the full deals ledger, before Explore knowledge base. */}
        <Button asChild variant="outline" data-testid="partner-view-all-deals">
          <Link to="/dashboard/partner/deals">
            <FileText className="h-4 w-4" aria-hidden /> View all deals
          </Link>
        </Button>
        <Button asChild variant="outline" data-testid="explore-sales-toolkit">
          <Link to="/dashboard/assets">
            <Library className="h-4 w-4" aria-hidden /> Explore {FEATURE_NAV_LABEL.toLowerCase()}
          </Link>
        </Button>
        {/* CHP-1005: optional (non-compulsory) training discovery, after the
            knowledge-base CTA. */}
        <Button asChild variant="outline" data-testid="browse-optional-training">
          <Link to="/dashboard/partner/training">
            <GraduationCap className="h-4 w-4" aria-hidden /> Browse optional training
          </Link>
        </Button>
        {/* CHP-788: compact secondary launch for the team-invite Sheet, gated on
            canInviteTeam. Replaces the removed promo card (CHP-790); label
            matches the Insights & Admin route for consistency. */}
        {canInviteTeam && (
          <Button
            variant="outline"
            onClick={() => setInviteOpen(true)}
            data-testid="partner-open-team-invites"
          >
            <UserPlus className="h-4 w-4" aria-hidden /> Invite new team member
          </Button>
        )}
      </div>

      {/* Metric cards — review-r2 (9c088dcc / 69af1cf9): the PO gets the SAME
          three cards as the BDM view (My expiring deals / Deals in review /
          conditional training), sourced from the team dashboard. The old
          four-card team-metrics row (pipeline / closed-YTD / registrations /
          incentive-pool) and its SPIF footnote were removed — the incentive
          pool referenced the cancelled SPIF programme. */}
      <PartnerMetricCards metrics={teamMetrics} training={trainingCard} />

      {/* Weighted to-do feed — Tier-1 team blockers (deep-link to the deal
          record), Tier-2 personal actions, Tier-3 enablement.
          TODO(followup #20): the Team Activity Hub messaging surface is not
          built, so a Tier-1 card deep-links to the relevant deal record (or
          member) instead of opening a messaging thread. */}
      <div className="mt-6">
        <PoTodoFeed
          items={feed}
          now={now}
          feedError={feedError}
          onRetryFeed={onRetryFeed}
          onOpenTeamDeal={(dealId) => setDrawerDeal({ id: dealId, readOnly: true })}
          onReviewExpiring={onReviewExpiring}
          onOpenOwnDeal={(dealId) => setDrawerDeal({ id: dealId, readOnly: false })}
          onResumeDraft={onResumeDraft}
          onOpenTraining={onOpenTraining}
          onDismissExpiring={onDismissExpiring}
          onDismissDraft={onDismissDraft}
          onDismissOutcome={onDismissOutcome}
          onSnoozeMeetingReward={onSnoozeMeetingReward}
          onStopMeetingReward={onStopMeetingReward}
        />
      </div>

      {/* CHP-790: the "Invite your team" promo card was removed — the compact
          "Invite new team member" button in the actions row above now triggers
          this Sheet. */}
      {canInviteTeam && accessToken && (
        <TeamInviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          token={accessToken}
          orgName={orgName}
          orgId={orgId}
        />
      )}

      {/* CHP-868: feed "view deal" opens the deal detail here, in place. */}
      <DealDetailDrawer
        dealId={drawerDeal?.id ?? null}
        readOnly={drawerDeal?.readOnly ?? false}
        onClose={() => setDrawerDeal(null)}
      />
    </div>
  );
}
